import { useVendorsApiClient } from 'vendors/api/useVendorsApiClient'
import { useQuery, UseQueryResult, useQueryClient, UseMutationResult, useMutation } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'

export const useAddVendor = () => {
  const { addVendor } = useVendorsApiClient()
  return useMutation({
    mutationFn: (payload: AddVendorRequestType) => addVendor(payload)
  })
}

export const useGetVendorDetailsByBusinessId = (
  businessId: string,
): UseQueryResult<VendorResponseType> => {
  const { getVendorDetailsByBusinessId } = useVendorsApiClient()

  return useQuery<VendorResponseType>({
    queryKey: ['vendor-details-by-businessId', businessId],
    queryFn: () => getVendorDetailsByBusinessId(businessId),
    refetchOnWindowFocus: false,
    enabled: businessId !== ''
  })
}

export const useGetVendorDetailsById = (vendorId: string): UseQueryResult<VendorResponseType> => {
  const { getVendorDetailsById } = useVendorsApiClient()
  return useQuery<VendorResponseType>({
    queryKey: ['vendor-details', vendorId],
    queryFn: () => getVendorDetailsById(vendorId),
    refetchOnWindowFocus: false,
    enabled: vendorId !== '',
    staleTime: 500000
  })
}

// onSuccess not tested yet because backend return no tenant relationship
export const useCompleteVendorFlow = (email: string): UseMutationResult<void, unknown, void, unknown> => {
  const { completeVendorFlow } = useVendorsApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => completeVendorFlow(),
    onSuccess: () => {
      queryClient.setQueryData(['is-vendor-completed-flow', email], true)
    }
  })
}

export const useIsVendorFlowCompleted = (email: string): UseQueryResult<boolean> => {
  const { getIsVendorFlowCompleted } = useVendorsApiClient()
  return useQuery<boolean>({
    queryKey: ['is-vendor-completed-flow', email],
    queryFn: () => getIsVendorFlowCompleted(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: email !== ''
  })
}

export const useGetVendorContactsById = (vendorId: string): UseQueryResult<VendorContactsResponseType> => {
  const { getVendorContactsById } = useVendorsApiClient()
  return useQuery({
    queryKey: ['vendor-contacts', vendorId],
    queryFn: () => getVendorContactsById(vendorId),
    refetchOnWindowFocus: false,
    enabled: vendorId !== ''
  })
}

export const useAddVendorContact = (
  vendorId: string
): UseMutationResult<unknown, unknown, AddVendorContactRequestType, unknown> => {
  const { addUserContact } = useVendorsApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contact: AddVendorContactRequestType) => addUserContact(contact, vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] })
    }
  })
}

export const useAddGeneralContact = (
  vendorId: string
): UseMutationResult<unknown, unknown, AddGeneralContactRequestType, unknown> => {
  const { addGeneralContact } = useVendorsApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contact: AddGeneralContactRequestType) => addGeneralContact(contact, vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] })
    }
  })
}

export const useUserContactReceivesRfps = (
  vendorId: string,
  userId: string
): UseMutationResult<unknown, unknown, void, unknown> => {
  const { userContactReceivesRfps } = useVendorsApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => userContactReceivesRfps(vendorId, userId),
    onMutate: () => {
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var userContacts = oldData.userContacts.map((item) => {
            if (item.id !== userId) {
              return item
            }

            return {
              ...item,
              receiveRequestForProposals: true
            }
          })

          return {
            ...oldData,
            userContacts
          }
        }
      )
    }
  })
}

export const useUserContactDoesNotReceiveRfps = (
  vendorId: string,
  userId: string
): UseMutationResult<unknown, unknown, void, unknown> => {
  const { userContactDoesNotReceiveRfps } = useVendorsApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => userContactDoesNotReceiveRfps(vendorId, userId),
    onMutate: () => {
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var userContacts = oldData.userContacts.map((item) => {
            if (item.id !== userId) {
              return item
            }

            return {
              ...item,
              receiveRequestForProposals: false
            }
          })

          return {
            ...oldData,
            userContacts
          }
        }
      )
    }
  })
}

export const useGetUserContact = (vendorId: string, userId: string): UseQueryResult<VendorUserContactGetType> => {
  const { getUserContact } = useVendorsApiClient()
  return useQuery({
    queryKey: ['vendor-user-contact', vendorId, userId],
    queryFn: () => getUserContact(vendorId, userId),
    enabled: vendorId !== '' && userId !== '',
    refetchOnWindowFocus: false
  })
}

export const useGeneralContactReceivesRfps = (
  vendorId: string,
  contactId: string
): UseMutationResult<unknown, unknown, void, unknown> => {
  const { generalContactReceivesRfps } = useVendorsApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => generalContactReceivesRfps(vendorId, contactId),
    onMutate: () => {
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var generalContacts = oldData.generalContacts.map((item) => {
            if (item.id !== contactId) {
              return item
            }

            return {
              ...item,
              receiveRequestForProposals: true
            }
          })

          return {
            ...oldData,
            generalContacts
          }
        }
      )
    }
  })
}

export const useGeneralContactDoesNotReceiveRfps = (
  vendorId: string,
  contactId: string
): UseMutationResult<unknown, unknown, void, unknown> => {
  const { generalContactDoesNotReceiveRfps } = useVendorsApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => generalContactDoesNotReceiveRfps(vendorId, contactId),
    onMutate: () => {
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var generalContacts = oldData.generalContacts.map((item) => {
            if (item.id !== contactId) {
              return item
            }

            return {
              ...item,
              receiveRequestForProposals: false
            }
          })

          return {
            ...oldData,
            generalContacts
          }
        }
      )
    }
  })
}

export const useUpdateUserContact = (
  vendorId: string,
  userId: string
): UseMutationResult<unknown, unknown, UpdateUserContactRequestType, unknown> => {
  const { updateUserContact } = useVendorsApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateUserContactRequestType) => updateUserContact(vendorId, userId, payload),
    onSuccess: (_, payload) => {
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var userContacts = oldData.userContacts.map((item) => {
            if (item.id !== userId) {
              return item
            }

            return {
              ...item,
              firstName: payload.firstName,
              lastName: payload.lastName,
              phone: payload.phone,
              responsibilityDescription: payload.responsibilityDescription,
              receiveRequestForProposals: payload.receiveRequestForProposals
            }
          })

          return {
            ...oldData,
            userContacts
          }
        }
      )

      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] })
    }
  })
}

export const useDeleteUserContact = (
  vendorId: string,
  contactId: string,
  contactEmail: string,
  email?: string
): UseMutationResult<unknown, unknown, void, unknown> => {
  const { deleteUserContact } = useVendorsApiClient()
  const { logout } = useAuth0()

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deleteUserContact(vendorId, contactId),
    onSuccess: () => {
      if (email === contactEmail) {
        logout({ returnTo: `${window.location.origin}/vendors/login` })
      }
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var userContacts = oldData.userContacts.filter((item) => item.id !== contactId)

          return {
            ...oldData,
            userContacts
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] })
    }
  })
}

export const useDeleteGeneralContact = (
  vendorId: string,
  contactId: string
): UseMutationResult<unknown, unknown, void, unknown> => {
  const { deleteGeneralContact } = useVendorsApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deleteGeneralContact(vendorId, contactId),
    onSuccess: () => {
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var generalContacts = oldData.generalContacts.filter((item) => item.id !== contactId)

          return {
            ...oldData,
            generalContacts
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] })
    }
  })
}

export const useGetGeneralContact = (
  vendorId: string,
  generalContactId: string
): UseQueryResult<GeneralContactResponseType> => {
  const { getVendorGeneralContact } = useVendorsApiClient()
  return useQuery({
    queryKey: ['vendor-general-contact', vendorId, generalContactId],
    queryFn: () => getVendorGeneralContact(vendorId, generalContactId),
    enabled: vendorId !== '' && generalContactId !== '',
    refetchOnWindowFocus: false
  })
}

export const useUpdateGeneralContact = (
  vendorId: string,
  generalContactId: string
): UseMutationResult<unknown, unknown, UpdateGeneralContactRequestType, unknown> => {
  const { updateGeneralContact } = useVendorsApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateGeneralContactRequestType) => updateGeneralContact(vendorId, generalContactId, payload),
    onSuccess: (_, payload) => {
      queryClient.setQueryData(
        ['vendor-contacts', vendorId],
        (
          oldData: VendorContactsResponseType = { userContacts: [], generalContacts: [] }
        ): VendorContactsResponseType => {
          var generalContacts = oldData.generalContacts.map((item) => {
            if (item.id !== generalContactId) {
              return item
            }

            return {
              ...item,
              email: payload.email,
              receiveRequestForProposals: payload.receiveRequestForProposals
            }
          })

          return {
            ...oldData,
            generalContacts
          }
        }
      )

      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] })
    }
  })
}

export const useUpdateVendorDetails = (
  vendorId: string
): UseMutationResult<unknown, unknown, UpdateVendorDetailsRequest, unknown> => {
  const { updateVendorBasicDetails } = useVendorsApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdateVendorDetailsRequest) => updateVendorBasicDetails(vendorId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-details', vendorId] })
    }
  })
}
